import { useMemo } from "react";
import { useApiQuery } from "./use-api";
import type { ApiSuccessResponse } from "../api-client";
import { useAuth } from "./use-auth";

/**
 * Fetches the current user's branch permissions and returns
 * which branches they are allowed to see/switch to.
 *
 * - If any permission row has branchId = null → user has access to ALL branches
 * - Otherwise → return array of allowed branchIds
 * - null = all branches (admin / owner / no restrictions)
 */

interface UserPermission {
  id: string;
  userId: string;
  companyId: string;
  companyName: string;
  companyAbbreviation: string;
  branchId: string | null;
  branchName: string | null;
}

export function useAllowedBranches() {
  const { user } = useAuth();

  const { data, isLoading } = useApiQuery<ApiSuccessResponse<UserPermission[]>>(
    `/core/user-permissions/${user?.id}`,
    {
      enabled: !!user?.id,
      staleTime: 2 * 60 * 1000, // cache 2 min
    },
  );

  const result = useMemo(() => {
    const permissions = data?.data ?? [];

    // No permissions at all → no restrictions (all branches)
    if (permissions.length === 0) {
      return { allowedBranchIds: null as string[] | null, hasAllBranches: true };
    }

    // If any permission has branchId = null → access to all branches
    const hasAllBranches = permissions.some((p) => !p.branchId);
    if (hasAllBranches) {
      return { allowedBranchIds: null as string[] | null, hasAllBranches: true };
    }

    // Collect unique branchIds
    const branchIds = [...new Set(permissions.map((p) => p.branchId).filter(Boolean))] as string[];
    return { allowedBranchIds: branchIds, hasAllBranches: false };
  }, [data]);

  return {
    ...result,
    isLoading,
  };
}
